import {
    Component,
    ChangeDetectionStrategy,
    inject,
    input,
    output,
    computed,
    linkedSignal,
    signal,
    viewChild,
    ElementRef,
} from "@angular/core";
import { Item } from "../../pojo/Item";
import { ObjectSearchAvail } from "./object.search.avail";
import { AvailLocationItems, AvailResourceItems } from "./s25.object.search.util";
import { EventOccUtil } from "../s25-event/s25-event-occurrences/s25-event-occurrence/s25.event.occurrence.util";
import { S25Reservation } from "../s25-event/ReservationI";
import { S25ObjectSearchUtil } from "./s25.object.search.util";
import { TaskCompetingUtil } from "../s25-task/task.competing.util";
import { WaitlistStore } from "../../stores/waitlist.store";
import { WaitlistEntry, WaitlistCreatRequest, WaitlistType } from "../../pojo/Waitlist";
import { S25Util } from "../../util/s25-util";
import { AriaLive } from "../../services/aria.live.service";
import { InstanceCount } from "../../util/s25.instance.count";
import { s25Resource } from "../../util/s25.resource";
import { S25CheckboxModule } from "../s25-checkbox/s25.checkbox.module";
import { S25ShiftSelectableModule } from "../s25-shift-select/s25.shift.selectable.module";
import { FormsModule } from "@angular/forms";

type ConflictFlags = {
    hasConflicts: boolean;
    isReqConflict: boolean;
    hasPerm: boolean;
    waitlistEnabled: boolean;
};

export type ObjectAddButtonViewState = {
    buttonText?: string;
    buttonClass?: string;
    buttonDisabled?: boolean;
    selectable?: boolean;
    showAddToWaitlist?: boolean;
};

@Component({
    selector: "s25-ng-object-add-button",
    host: {
        class: "object-add-button-wrapper",
    },
    template: `
        @if (this.data() && !this.conflictFlags.isLoading()) {
            @if (state().selectable && allowSelection()) {
                <s25-ng-checkbox
                    s25-ng-shift-selectable
                    [shiftSelectIndex]="instanceId"
                    [shiftSelectGroup]="'AddItems'"
                    [value]="isChecked()"
                    (valueChange)="onCheckBoxClick($event)"
                    [ariaLabel]="'Select Item'"
                    [noLabel]="true"
                ></s25-ng-checkbox>
            }

            @if (itemTypeId() === ItemIds.Resource) {
                @if (canReserve() && !hasAddedItem()) {
                    <input
                        #quantityInput
                        class="c-input"
                        aria-label="Number to reserve"
                        type="number"
                        min="0"
                        [max]="max()"
                        id="quantity"
                        [ngModel]="quantity()"
                        (ngModelChange)="onQuantityChange($event)"
                    />
                } @else {
                    <div class="disabled-reserve" [attr.title]="disabledQuantityTitle()">
                        <input
                            class="c-input"
                            [attr.aria-label]="disabledQuantityAriaLabel()"
                            type="number"
                            id="quantity"
                            disabled
                            [ngModel]="mixedOccQuantities() ? null : quantity()"
                            [attr.placeholder]="disabledQuantityPlaceholder()"
                        />
                    </div>
                }
            }

            @if (state().showAddToWaitlist) {
                <button
                    [class]="'reserve-button aw-button ' + state().buttonClass"
                    (click)="addToWaitlist()"
                    [disabled]="state().buttonDisabled || isCreatingWaitlist()"
                    [attr.aria-label]="'Add to Waitlist'"
                >
                    @if (isCreatingWaitlist()) {
                        Adding to Waitlist...
                    } @else {
                        Add to Waitlist
                    }
                </button>
            } @else {
                <button
                    [class]="'reserve-button aw-button ' + state().buttonClass"
                    (click)="click()"
                    [disabled]="state().buttonDisabled"
                >
                    {{ state().buttonText }}
                </button>
            }
        }
    `,
    styles: [
        `
            input[type="number"] {
                /* important because event form has aggressive styles */
                width: 80px !important;
                min-width: unset !important;
                max-width: unset !important;
            }

            :host {
                display: flex !important;
                flex-direction: row;
                gap: 0.875em; /* Adjust the space between the columns */
                align-items: center; /* Align items vertically in the middle */
                justify-content: space-between;
                width: 100%; /* Ensure the wrapper spans the available width */
            }

            .c-input[disabled] {
                /*
                 * disabled inputs do not trigger click events, which trips up
                 * our dropdown outside-click handling
                 */
                pointer-events: none;
            }
            .disabled-reserve {
                cursor: not-allowed;
            }
            .reserve-button {
                margin: auto;
                word-wrap: normal;
                width: min-content;
            }
            s25-ng-info-message s25-ng-icon svg {
                fill: currentColor;
                color: #8c6922;
            }

            s25-ng-info-message {
                color: #8c6922;
            }
        `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25CheckboxModule, S25ShiftSelectableModule, FormsModule],
})
export class S25ObjectAddButtonComponent extends InstanceCount {
    readonly data = input<AvailLocationItems | AvailResourceItems>();
    readonly itemTypeId = input<Item.Ids>(Item.Ids.Location);
    readonly occ = input<S25Reservation>();
    readonly allowSelection = input<boolean>(true);
    readonly isEventForm = input<boolean>(false);
    readonly onButtonClick = output<AddButtonOutput>();

    private readonly waitlistStore = inject(WaitlistStore);
    readonly quantityInput = viewChild("quantityInput", { read: ElementRef<HTMLInputElement> });

    readonly conflictFlags = s25Resource({
        params: () => this.data(),
        loader: async ({ params: data }): Promise<ConflictFlags> => {
            const waitlistEnabled = await TaskCompetingUtil.waitlistEnabled(undefined, data?.waitlist, false);
            const hasConflicts = (data!.checkedObj?.conflicts?.length ?? 0) > 0 || data!.hasConflicts;
            const isReqConflict = S25ObjectSearchUtil.isRequestConflict(data!) && waitlistEnabled;
            const hasPerm = ["C", "F"].includes(data!.assign_perm ?? "");
            return { hasConflicts, isReqConflict, hasPerm, waitlistEnabled };
        },
    });

    readonly getAddedItems = computed(() => {
        const object =
            this.itemTypeId() === Item.Ids.Location ? this.occ()?.locations?.[0] : this.occ()?.resources?.[0];
        // Draft prefs seed quantity only — they must not count as already-added (ANG-7556).
        return [...(object?.requested ?? []), ...(object?.reserved ?? [])];
    });
    readonly draftResourceItem = computed(() => {
        const resourceId = this.data()?.resource_id;
        if (resourceId == null) return undefined;
        return this.occ()?.resources?.[0]?.draft?.find((item) => item.itemId === resourceId);
    });
    readonly max = computed(() => {
        if (this.itemTypeId() === Item.Ids.Location || !this.data()) {
            return 1;
        }
        const data = this.data()!;
        if (data.quantity === "Unlimited") {
            return 65536;
        }
        return data.maxAvailable ?? (typeof data.quantity === "number" ? data.quantity : 1);
    });
    readonly noAvailableDates = computed(() => {
        const availableDates = this.data()?.checkedObj?.availableDatesWithOverrides;
        return S25Util.isDefined(availableDates) && availableDates <= 0;
    });
    readonly hasPartialRealAvailability = computed(() => {
        const checkedObj = this.data()?.checkedObj;
        if (!checkedObj?.totalDatesChecked) return false;
        const availableDates = checkedObj.availableDatesWithOverrides;
        if (!S25Util.isDefined(availableDates)) return false;
        return availableDates > 0 && availableDates < checkedObj.totalDatesChecked;
    });
    readonly canReserve = computed((): boolean => {
        const flags = this.conflictFlags.value();
        return !this.noAvailableDates() || (!!flags?.waitlistEnabled && flags.isReqConflict);
    });
    readonly hasConfirmedConflicts = computed(
        () => this.data() && S25ObjectSearchUtil.hasConfirmedReservationConflicts(this.data()!),
    );
    readonly isAlreadyOnWaitlist = computed(() => this.checkAlreadyOnWaitlist());

    // This is a linkedSignal so the "Added" text can appear immediately. There
    // are probably better ways to do this, but might require refactoring results.list
    readonly hasAddedItem = linkedSignal(() => {
        const data = this.data();
        if (!data) return false;
        const idKey = this.itemTypeId() === Item.Ids.Location ? "space_id" : "resource_id";
        return this.getAddedItems().some((item) => item.itemId === data[idKey]);
    });

    readonly addedResourceItem = computed(() => {
        const resourceId = this.data()?.resource_id;
        if (resourceId == null) return undefined;
        return this.getAddedItems().find((item) => item.itemId === resourceId);
    });

    readonly mixedOccQuantities = computed(() => {
        if (this.hasAddedItem()) {
            const addedItem = this.addedResourceItem();
            return !!(addedItem && "mixedOccQuantities" in addedItem && addedItem.mixedOccQuantities);
        }
        const draftItem = this.draftResourceItem();
        return !!draftItem?.mixedOccQuantities;
    });

    readonly disabledQuantityPlaceholder = computed(() => {
        if (this.hasAddedItem()) {
            return this.mixedOccQuantities() ? "Mixed" : null;
        }
        return "0";
    });

    readonly disabledQuantityTitle = computed(() =>
        this.mixedOccQuantities() ? "Different quantities per occurrence" : null,
    );

    readonly disabledQuantityAriaLabel = computed(() => {
        return this.hasAddedItem() ? "Number reserved" : "Number to reserve";
    });

    /**
     * state is set up as a linkedSignal because of how jenky addToWaitlist is.
     * if you have an idea to clean up addToWaitlist, we could convert this to
     * a computed signal :D
     */
    readonly state = linkedSignal((): ObjectAddButtonViewState => this.getState());

    readonly quantity = linkedSignal(() => {
        const addedItem = this.addedResourceItem();
        if (this.itemTypeId() === Item.Ids.Resource && addedItem) {
            if (this.mixedOccQuantities()) {
                return undefined;
            }
            return "quantity" in addedItem ? (addedItem.quantity ?? 1) : 1;
        }
        const draftItem = this.draftResourceItem();
        if (this.itemTypeId() === Item.Ids.Resource && draftItem) {
            if (draftItem.mixedOccQuantities) {
                return undefined;
            }
            return draftItem.quantity ?? 1;
        }
        return this.canReserve() ? 1 : undefined;
    });
    readonly isChecked = signal(false);
    readonly isCreatingWaitlist = signal(false);

    readonly ItemIds = Item.Ids;

    private getState() {
        if (!this.data()) {
            return { selectable: true };
        }

        if (this.hasAddedItem()) {
            return {
                buttonText: "Added",
                buttonClass: "aw-button--default",
                buttonDisabled: true,
            };
        }

        if (this.canReserve()) {
            return {
                buttonText: this.getReserveLabel(),
                buttonClass: this.getReserveClass(),
                selectable: true,
            };
        }

        if (this.canAddToWaitlist()) {
            if (this.isAlreadyOnWaitlist()) {
                return {
                    buttonText: "Added to Waitlist",
                    buttonClass: "aw-button--success",
                    buttonDisabled: true,
                };
            }
            return {
                buttonText: "Add to Waitlist",
                buttonClass: "aw-button--warning",
                selectable: true,
                showAddToWaitlist: true,
            };
        }

        return {
            buttonText: "Unavailable",
            buttonClass: "aw-button--default",
            buttonDisabled: true,
        };
    }

    click() {
        this.hasAddedItem.set(true);
        return this.doneCheckBoxClick(false, false);
    }

    onCheckBoxClick(checked: boolean) {
        this.isChecked.set(checked);
        return this.doneCheckBoxClick(checked, true);
    }

    async doneCheckBoxClick(isAdd: boolean, multiSelected: boolean) {
        const objType = this.itemTypeId() === Item.Ids.Location ? "location" : "resource";
        const requestQty = this.quantity();
        if (!requestQty) return;
        const data = EventOccUtil.normalizeAddRsrvWSData(
            this.data() ?? null,
            this.getAddedItems(),
            objType,
            requestQty,
        );
        this.onButtonClick.emit({
            data,
            isAdd,
            multiSelected,
            waitlistItem: this.state().showAddToWaitlist,
        });
    }

    onQuantityChange(value: number) {
        this.quantity.set(Math.min(value, this.max()));
        // force the input element to match the quantity signal.
        // this seems to be relevant only for windows machines
        setTimeout(() => {
            if (this.quantityInput()) {
                this.quantityInput()!.nativeElement.value = String(this.quantity());
            }
        });
        if (this.isChecked()) this.doneCheckBoxClick(true, true);
    }

    /**
     * Add the current reservation to the waitlist for all blocking confirmed reservations.
     * Creates a waitlist entry for each blocking reservation.
     */
    async addToWaitlist() {
        const data = this.data();
        const occ = this.occ();
        if (!data || !occ?.itemId || this.itemTypeId() !== Item.Ids.Location) {
            return;
        }

        const confirmedConflicts = S25ObjectSearchUtil.getConfirmedReservationConflicts(data);

        if (confirmedConflicts.length === 0) {
            return;
        }

        this.isCreatingWaitlist.set(true);
        this.patchState({ buttonDisabled: true });

        const spaceId = data.space_id;
        const waitingRsrvId = occ.itemId;
        const waitlistEntries: WaitlistEntry[] = [];

        const errors: string[] = [];

        try {
            // Create waitlist entries for each blocking reservation
            // Use event-based resolution: conflictId is event_id, conflictStart/conflictEnd are reservation dates

            for (const conflict of confirmedConflicts) {
                const blockingEventId = conflict.conflictId;
                if (!blockingEventId || blockingEventId <= 0) {
                    continue;
                }

                // conflictStart and conflictEnd are Date objects from the conflict
                const conflictStart = conflict.conflictStart;
                const conflictEnd = conflict.conflictEnd;

                if (!conflictStart || !conflictEnd) {
                    console.warn(`Conflict missing start/end dates for event ${blockingEventId}, skipping`);
                    continue;
                }

                try {
                    // Use event-based resolution: send blockingEventId + conflict times
                    // Backend will resolve to blockingRsrvId by matching event_id + room_id + time overlap

                    const entry: WaitlistCreatRequest = {
                        request: {
                            waitlists: [
                                {
                                    spaceId,
                                    blockingEventId:
                                        typeof blockingEventId === "string"
                                            ? parseInt(blockingEventId)
                                            : blockingEventId,
                                    waiting: [
                                        {
                                            rsrvId: waitingRsrvId,
                                            conflictStartDt: conflictStart,
                                            conflictEndDt: conflictEnd,
                                        },
                                    ],
                                },
                            ],
                        },
                    };
                    const created = await this.waitlistStore.createWaitlistEntry(entry);
                    waitlistEntries.push(created);
                } catch (error: unknown) {
                    // Extract error message from API response
                    const errorMessage = S25Util.errorText(
                        error,
                        `Failed to add to waitlist for event ${blockingEventId}`,
                    );

                    errors.push(errorMessage);
                    console.error(`Failed to create waitlist entry for blocking event ${blockingEventId}:`, error);
                    // Continue with other entries even if one fails
                }
            }

            // Display errors if any occurred
            if (errors.length > 0) {
                const errorMsg = errors.length === 1 ? errors[0] : `Failed to add to waitlist:\n${errors.join("\n")}`;
                S25Util.showError(errorMsg, "Error adding to waitlist");
                AriaLive.announce("Failed to add to waitlist. See error message for details.", true);
            }

            if (waitlistEntries.length > 0) {
                const successMsg = `Added to waitlist for ${waitlistEntries.length} ${waitlistEntries.length === 1 ? "reservation" : "reservations"}`;
                AriaLive.announce(successMsg, true);

                // If some succeeded and some failed, show partial success
                if (errors.length > 0) {
                    this.patchState({
                        buttonText: `Added (${waitlistEntries.length}/${confirmedConflicts.length})`,
                        buttonClass: "aw-button--warning",
                    });
                } else {
                    // All succeeded
                    this.patchState({
                        buttonText: "Added to Waitlist",
                        buttonClass: "aw-button--success",
                    });
                    // Emit to trigger modal close via parent chain
                    this.onButtonClick.emit({ data: [], isAdd: false, multiSelected: false, closeModal: true });
                }
                this.patchState({
                    buttonDisabled: true,
                    showAddToWaitlist: false,
                });
            } else {
                // All failed
                this.isCreatingWaitlist.set(false);
                this.patchState({ buttonDisabled: false });
            }
        } catch (error: any) {
            const errorMessage = S25Util.errorText(error, "Failed to add to waitlist. Please try again.");
            S25Util.showError(errorMessage, "Error adding to waitlist");
            AriaLive.announce("Failed to add to waitlist. See error message for details.", true);
            this.isCreatingWaitlist.set(false);
            this.patchState({ buttonDisabled: false });
        }
    }

    revertState() {
        if (!this.data()) return;
        this.conflictFlags.resource.reload();
        this.isChecked.set(false);
        const data = this.data()!;
        const idKey = this.itemTypeId() === Item.Ids.Location ? "space_id" : "resource_id";
        const rowItemId = data[idKey];
        this.hasAddedItem.set(this.getAddedItems().some((added) => added.itemId === rowItemId));
    }

    selectItem() {
        // Select/check this item (for multiSelect mode on successful retry)
        this.isChecked.set(true);
    }

    /**
     * Check if this room is already on the waitlist for this reservation.
     */
    private checkAlreadyOnWaitlist() {
        const data = this.data();
        const occ = this.occ();
        if (!data?.space_id || !occ?.itemId || !occ?.waitlistRsrvEntries?.length) {
            return false;
        }

        const spaceId = data.space_id;
        const rsrvId = occ.itemId;

        return occ.waitlistRsrvEntries.some((entry) => {
            if (entry.spaceId !== spaceId) return false;
            return entry.waiting?.some((w) => w.rsrvId === rsrvId);
        });
    }

    patchState(partialState: Partial<ObjectAddButtonViewState>) {
        this.state.set({ ...this.state(), ...partialState });
    }

    private canAddToWaitlist(): boolean {
        if (this.isEventForm()) {
            return false;
        }
        const data = this.data();
        const flags = this.conflictFlags.value();
        return (
            !!flags?.waitlistEnabled &&
            !!this.hasConfirmedConflicts() &&
            this.itemTypeId() === Item.Ids.Location &&
            !!this.occ()?.itemId &&
            !!data?.space_id &&
            data.waitlist === WaitlistType.Assign
        );
    }

    private getReserveLabel(): string | undefined {
        const flags = this.conflictFlags.value();
        if (!flags) return undefined;
        const permLabel = flags.hasPerm ? "Reserve" : "Request";
        return this.hasPartialRealAvailability() ? `${permLabel} Available` : permLabel;
    }

    private getReserveClass(): string | undefined {
        const flags = this.conflictFlags.value();
        if (!flags) return undefined;
        if (flags.hasConflicts || flags.isReqConflict) {
            return "aw-button--warning";
        }
        if (flags.hasPerm) {
            return "aw-button--success";
        }
        return "aw-button--primary";
    }
}

export type AddButtonOutput = {
    data?: ObjectSearchAvail.AddedItem[];
    isAdd?: boolean;
    multiSelected?: boolean;
    closeModal?: boolean;
    waitlistItem?: boolean;
};
